import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription } from 'rxjs';
import { Globals } from '../../_models/globals';

import { NotificationService } from '../../_services/notification.service';
import { BillingSupplierService } from './billing-supplier.service';
import { SupportBillingSupplierDetailComponent } from './_components/billing-supplier-detail/billing-supplier-detail.component';
import { SupportGridview, SupportGridviewTableBodyCell, SupportGridviewTemplateCell } from '../_models/supportGridview';
import { ApiResponseModel } from 'app/_models/apiResponseModel';

import { HoldingPaymentType } from 'app/_models/holdingPaymentType';
import { BillingSupplierData } from './_models/billingSupplierData';
import { BillingStatusEnum } from 'app/_models/_enums/billingStatus.enum';

declare const isEmpty: any;

@Component({
    selector: 'app-billing-supplier', templateUrl: './billing-supplier.component.html', styleUrls: ['./billing-supplier.component.scss'],
    standalone: false
})
export class SupportBillingSupplierComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild('paymentTypeTemplate') paymentTypeTemplate: TemplateRef<any>;
    @ViewChild('lastDateTemplate') lastDateTemplate: TemplateRef<any>;
    @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
    @ViewChild('templateBillingSupplierDetail') public billingHoldingDetailModal: SupportBillingSupplierDetailComponent;
    private _sortSubscription: Subscription;

    public config: SupportGridview = new SupportGridview();
    public templatesArray: SupportGridviewTemplateCell[] = [];

    public filter: string = '';

    billingSupplierDataList: BillingSupplierData[];
    billingSupplierDataListFiltered: BillingSupplierData[];

    billingSupplierErrorQuantity: number = 0;
    billingSupplierErrorEmailQuantity: number = 0;
    billingSupplierSuccessQuantity: number = 0;
    billingSupplierSuccessEmailQuantity: number = 0;
    billingSupplierTotalQuantity: number = 0;
    billingSupplierLastExecution: Date;
    billingSupplierLastEmailExecution: Date;

    canSendEmails: boolean = false;
    hadAlreadySentEmails: boolean = false;
    isOnEmailPage: boolean = false;

    constructor(private billingSupplierService: BillingSupplierService, private notificationService: NotificationService, private globals: Globals, private route: ActivatedRoute, private ngxLoader: NgxUiLoaderService) {}

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.templatesArray = [
                { name: 'lastDateTemplate', template: this.lastDateTemplate },
                { name: 'paymentTypeTemplate', template: this.paymentTypeTemplate },
                { name: 'statusTemplate', template: this.statusTemplate },
            ];
        })
    }

    ngOnDestroy(): void {
        if (!this._sortSubscription?.closed)
            this._sortSubscription.unsubscribe();
    }

    ngOnInit(): void {
        this._sortSubscription = this.billingSupplierService.sortDataValue$.subscribe(value => this.config.sort = value);

        this.config.set({
            actions: {
                edit: { enable: false },
                action: { enable: !this.isOnEmailPage, confirmationText: 'support.faturamento-fornecedores.table.icon-button.billing.confirm', tooltipText: 'support.faturamento-fornecedores.table.icon-button.billing.tooltip' },
                email: { enable: this.isOnEmailPage, confirmationText: 'support.faturamento-fornecedores.table.icon-button.email.confirm', tooltipText: 'support.faturamento-fornecedores.table.icon-button.email.tooltip', disabledCondition: (item: BillingSupplierData) => !this.hadAlreadySentEmails || (item.statusEmail == BillingStatusEnum.OnGoing || item.statusEmail == BillingStatusEnum.Pending) },
            },
            emptyText: { isHtml: false, value: 'support.faturamento-fornecedores.table.empty.before-search' },
            icon: { iconName: 'search', tooltipText: 'support.faturamento-fornecedores.table.icon.details.tooltip' },
            list: [],
            sort: this.billingSupplierService.getSortDataValue(),
            table: {
                body: [
                    new SupportGridviewTableBodyCell('col col-0-5 text-center', 'holdingId'),
                    new SupportGridviewTableBodyCell('col col-0-5 text-center', 'superlogicaId'),
                    new SupportGridviewTableBodyCell('col col-2 truncate-gridview', 'holdingName'),
                    new SupportGridviewTableBodyCell('col col-1 text-end', 'monthlyFee', 'currency'),
                    new SupportGridviewTableBodyCell('col col-1-25 text-end', 'commissionPayable', 'html', null, 'paymentTypeTemplate'),
                    new SupportGridviewTableBodyCell('col col-0-75 text-end', 'cashbackPayable', 'currency'),
                    new SupportGridviewTableBodyCell('col col-0-75 text-end', 'disputedValue', 'negative-currency'),
                    new SupportGridviewTableBodyCell('col col-0-75 text-end', 'totalPayable', 'currency'),
                    new SupportGridviewTableBodyCell('col col-0-75 text-center', '', 'html', null, 'lastDateTemplate'),
                    new SupportGridviewTableBodyCell('col col-1 text-center', '', 'html', null, 'statusTemplate'),
                ],
                header: []
            }
        });

        let isEmailPage = this.route.snapshot.url.some(segment => segment.path === 'email');
        this.changePage(isEmailPage)
        this.loadScreen();
    }

    changePage(emailParam?: boolean) {
        this.isOnEmailPage = emailParam ?? !this.isOnEmailPage;

        if (emailParam == null)
            history.pushState({}, '', '/suporte/faturamento/fornecedores' + (this.isOnEmailPage ? '/email' : ''));

        this.handleTableChange();
    }

    async executeAllEmails() {
        const confirm = await this.notificationService.showConfirmationDialog('support.faturamento-fornecedores.header.buttons.email.confirm');

        if (!confirm)
            return;

        this.ngxLoader.startLoader('loader-principal');
        var supplierHoldingIds = this.billingSupplierDataList.map(holding => holding.holdingId);

        this.billingSupplierService.executeEmail(supplierHoldingIds).subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                this.loadScreen();
                this.notificationService.showSuccessToastr('support.faturamento-fornecedores.messages.success.execute-email-all-pending', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            },
            error: (error) => {
                this.notificationService.showErrorToastr('support.faturamento-fornecedores.messages.error.execute-email-all-pending', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    executeSelectedEmail(holding: BillingSupplierData) {
        this.ngxLoader.startLoader('loader-principal');

        this.billingSupplierService.executeEmail([holding.holdingId]).subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                this.loadScreen();
                this.notificationService.showSuccessToastr('support.faturamento-fornecedores.messages.success.execute-email', { translateMessage: true });
            },
            error: (error) => {
                this.notificationService.showErrorToastr('support.faturamento-fornecedores.messages.error.execute-email', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    async executePendingBilling() {
        const confirm = await this.notificationService.showConfirmationDialog('support.faturamento-fornecedores.header.buttons.faturar.confirm');

        if (!confirm)
            return;

        this.ngxLoader.startLoader('loader-principal');

        var pendingHoldings = this.billingSupplierDataList.filter(holding => holding.status == BillingStatusEnum.Pending || holding.status == BillingStatusEnum.Error);
        var supplierHoldingIds = pendingHoldings.map(holding => holding.holdingId);

        this.billingSupplierService.executeBilling(supplierHoldingIds).subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                this.loadScreen();
                this.notificationService.showSuccessToastr('support.faturamento-fornecedores.messages.success.execute-billing-pending', { translateMessage: true });
            },
            error: (error) => {
                this.notificationService.showErrorToastr('support.faturamento-fornecedores.messages.error.execute-billing-pending', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    executeSelectedBilling(holding: BillingSupplierData) {
        this.ngxLoader.startLoader('loader-principal');

        this.billingSupplierService.executeBilling([holding.holdingId]).subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                this.loadScreen();
                this.notificationService.showSuccessToastr('support.faturamento-fornecedores.messages.success.execute-billing', { translateMessage: true });
            },
            error: (error) => {
                this.notificationService.showErrorToastr('support.faturamento-fornecedores.messages.error.execute-billing', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    filterBillingSupplierDataList() {
        if (isEmpty(this.billingSupplierDataList))
            return [];

        if (isEmpty(this.filter))
            return this.billingSupplierDataList;

        var term: string = this.filter.toLowerCase();

        return this.billingSupplierDataList.filter(holding => {
            return holding.holdingId.toString().startsWith(term)
                || holding.superlogicaId?.toString().startsWith(term)
                || holding.holdingName.toLowerCase().includes(term);
        });
    }

    getLastExecutionDate(holdingList: BillingSupplierData[]): Date {
        var lastExecutionDate = holdingList.reduce((prev: Date, current) => {
            if (!prev || (current.lastBilling && current.lastBilling > prev))
                return current.lastBilling;

            return prev;
        }, null);

        return lastExecutionDate;
    }

    getLastEmailExecutionDate(holdingList: BillingSupplierData[]): Date {
        var lastExecutionDate = holdingList.reduce((prev: Date, current) => {
            if (!prev || (current.lastEmail && current.lastEmail > prev))
                return current.lastEmail;

            return prev;
        }, null);

        return lastExecutionDate;
    }

    handleFormatHoldingList(holdingList: BillingSupplierData[]) {
        holdingList.forEach(holding => {
            var billingStatus = BillingStatusEnum[holding.status].toLowerCase();
            var emailStatus = BillingStatusEnum[holding.statusEmail].toLowerCase();

            holding.statusClass = billingStatus;
            holding.statusDescription = this.globals.translate(`support.faturamento-fornecedores.table.status.${billingStatus}`);
            holding.statusEmailClass = emailStatus;
            holding.statusEmailDescription = this.globals.translate(`support.faturamento-fornecedores.table.status.${emailStatus}`);
        })

        this.billingSupplierTotalQuantity   = holdingList.length;

        this.billingSupplierSuccessQuantity = holdingList.filter(holding => holding.status == BillingStatusEnum.Success).length;
        this.billingSupplierErrorQuantity   = holdingList.filter(holding => holding.status == BillingStatusEnum.Error).length;
        this.billingSupplierLastExecution   = this.getLastExecutionDate(holdingList);

        this.billingSupplierSuccessEmailQuantity = holdingList.filter(holding => holding.statusEmail == BillingStatusEnum.Success).length;
        this.billingSupplierErrorEmailQuantity = holdingList.filter(holding => holding.statusEmail == BillingStatusEnum.Error).length;
        this.billingSupplierLastEmailExecution   = this.getLastEmailExecutionDate(holdingList);


        return holdingList;
    }

    handleTableChange() {
        this.config.actions = {
            ...this.config.actions,
            action: { enable: !this.isOnEmailPage, confirmationText: 'support.faturamento-fornecedores.table.icon-button.billing.confirm', tooltipText: 'support.faturamento-fornecedores.table.icon-button.billing.tooltip', type: 'icon' },
            email: { enable: this.isOnEmailPage, confirmationText: 'support.faturamento-fornecedores.table.icon-button.email.confirm', tooltipText: 'support.faturamento-fornecedores.table.icon-button.email.tooltip', type: 'icon', disabledCondition: (item: BillingSupplierData) => !this.hadAlreadySentEmails || (item.statusEmail == BillingStatusEnum.OnGoing || item.statusEmail == BillingStatusEnum.Pending) },
        }

        this.config.table.header = [
            { className: 'col col-0-5 text-center', sortName: 'holdingId',  value: 'support.faturamento-fornecedores.table.title.id' },
            { className: 'col col-0-5 text-center', sortName: 'superlogicaId', value: 'support.faturamento-fornecedores.table.title.superlogica-id' },
            { className: 'col col-2', sortName: 'holdingName', value: 'support.faturamento-fornecedores.table.title.fornecedor' },
            { className: 'col col-1 text-end', sortName: 'monthlyFee', value: 'support.faturamento-fornecedores.table.title.mensalidade' },
            { className: 'col col-1-25 text-end', sortName: 'commissionPayable', value: 'support.faturamento-fornecedores.table.title.valor-pagamento.title', subtitle: 'support.faturamento-fornecedores.table.title.valor-pagamento.subtitle' },
            { className: 'col col-0-75 text-end', sortName: 'cashbackPayable', value: 'support.faturamento-fornecedores.table.title.repasse-cashback.title', subtitle: 'support.faturamento-fornecedores.table.title.repasse-cashback.subtitle' },
            { className: 'col col-0-75 text-end', sortName: 'disputedValue', value: 'support.faturamento-fornecedores.table.title.reembolso.title', subtitle: 'support.faturamento-fornecedores.table.title.reembolso.subtitle' },
            { className: 'col col-0-75 text-end', sortName: 'totalPayable', value: 'support.faturamento-fornecedores.table.title.total' },
            { className: 'col col-0-75 text-center', sortName: this.isOnEmailPage ? 'lastEmail' : 'lastBilling', value: 'support.faturamento-fornecedores.table.title.ultimo-faturamento' },
            { className: 'col col-1 text-center', sortName: 'statusDescription', value: 'support.faturamento-fornecedores.table.title.status' },
            { className: 'col col-0-5 text-center', value: '' }
        ]
    }

    loadScreen() {
        this.ngxLoader.startLoader('loader-principal');

        this.billingSupplierService.getAll().subscribe({
            next: (response: ApiResponseModel<BillingSupplierData[]>) => {
                this.billingSupplierDataList = this.handleFormatHoldingList(response.result);
                this.setSupportGridViewData();

                this.canSendEmails = !this.billingSupplierDataList.some((data) => data.status !== BillingStatusEnum.Success);
                this.hadAlreadySentEmails = this.billingSupplierDataList.some((data) => data.statusEmail !== BillingStatusEnum.Pending);

                if (!this.canSendEmails && this.isOnEmailPage) {
                    this.changePage();
                }

                this.ngxLoader.stopLoader('loader-principal');
            },
            error: (error) => {
                this.notificationService.showErrorToastr('support.faturamento-fornecedores.messages.error.load', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-principal');
            }
        });
    }

    onSearch($event: string) {
        if (isEmpty(this.billingSupplierDataList))
            return [];

        this.filter = $event;
        this.config.emptyText = { isHtml: false, value: 'support.faturamento-fornecedores.table.empty.' + (isEmpty(this.filter) ? 'before-search' : 'after-search') },

        this.setSupportGridViewData();
    }

    setSupportGridViewData() {
        this.config.list = this.filterBillingSupplierDataList();
    }
}
